import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidResetSession, setIsValidResetSession] = useState(false);
  
  const { updatePassword, user, session } = useAuth();

  // Supabase handles the authentication automatically through the verification link

  useEffect(() => {
    let cancelled = false;

    const initializeResetSession = async () => {
      setInitializing(true);
      setError('');

      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            throw exchangeError;
          }
        } else if (accessToken && refreshToken) {
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (setSessionError) {
            throw setSessionError;
          }
        }

        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          throw sessionError;
        }

        if (!cancelled) {
          if (data?.session) {
            setIsValidResetSession(true);
            window.history.replaceState({}, document.title, '/reset-password');
          } else {
            setError('Invalid or expired reset link. Please request a new password reset.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Invalid or expired reset link. Please request a new password reset.');
        }
      } finally {
        if (!cancelled) {
          setInitializing(false);
        }
      }
    };

    initializeResetSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (user && session) {
      setIsValidResetSession(true);
    }
  }, [user, session]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await updatePassword(password);
      
      if (error) {
        throw error;
      }
      
      setSuccess(true);
    } catch (error) {
      setError(error.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  // If password was successfully updated, redirect to login after a delay
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      window.location.href = '/login';
    }, 3000);
    return () => clearTimeout(timer);
  }, [success]);

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom align="center">
        Reset Your Password
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Password updated successfully! Redirecting to login...
        </Alert>
      )}

      {initializing && !success && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      
      {isValidResetSession && !success && !initializing && (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="New Password"
            type="password"
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading || !password || !confirmPassword}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Password'}
          </Button>
        </Box>
      )}
      
      {error.includes('Invalid or expired') && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => window.location.href = '/login'}
          >
            Back to Login
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default ResetPassword;
